import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Trash2 } from 'lucide-react';
interface CritereEvaluation {
  id?: string;
  nom: string;
  description: string;
  ponderation_pct: number;
  contenu_html: string;
  ordre: number;
  evaluation_id?: string;
}
interface CriteresEvaluationProps {
  aoId: string;
  evaluationId?: string;
  evaluationName?: string;
  initialCriteres?: CritereEvaluation[];
  onCriteresChange: (criteres: CritereEvaluation[]) => void;
}
export const CriteresEvaluation: React.FC<CriteresEvaluationProps> = ({
  aoId,
  evaluationId,
  evaluationName,
  initialCriteres = [],
  onCriteresChange
}) => {
  const [criteres, setCriteres] = useState<CritereEvaluation[]>(initialCriteres);
  const [totalPonderation, setTotalPonderation] = useState(0);
  useEffect(() => {
    const haveSameContent = (a: CritereEvaluation[], b: CritereEvaluation[]) => {
      if (a === b) return true;
      if (a.length !== b.length) return false;
      return JSON.stringify(a) === JSON.stringify(b);
    };
    setCriteres(prev => {
      if (haveSameContent(prev, initialCriteres)) {
        return prev;
      }
      return initialCriteres;
    });
  }, [initialCriteres]);
  useEffect(() => {
    const total = criteres.reduce((sum, critere) => sum + critere.ponderation_pct, 0);
    setTotalPonderation(total);
  }, [criteres]);
  useEffect(() => {
    // Debounce and compare to prevent re-render loops
    const timeoutId = setTimeout(() => {
      console.log('CriteresEvaluation: Critères changés:', criteres);
      onCriteresChange(criteres);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [criteres, onCriteresChange]);
  const addCritere = () => {
    const newCritere: CritereEvaluation = {
      nom: '',
      description: '',
      ponderation_pct: 0,
      contenu_html: '',
      ordre: criteres.length + 1,
      evaluation_id: evaluationId
    };
    setCriteres([...criteres, newCritere]);
  };
  const updateCritere = (index: number, field: keyof CritereEvaluation, value: string | number) => {
    const updatedCriteres = [...criteres];
    updatedCriteres[index] = {
      ...updatedCriteres[index],
      [field]: value
    };
    setCriteres(updatedCriteres);
  };
  const removeCritere = (index: number) => {
    const updatedCriteres = criteres.filter((_, i) => i !== index);
    // Update ordre
    updatedCriteres.forEach((critere, i) => {
      critere.ordre = i + 1;
    });
    setCriteres(updatedCriteres);
  };
  const distributeEqually = () => {
    if (criteres.length === 0) return;
    const equalWeight = Math.floor(100 / criteres.length);
    const remainder = 100 % criteres.length;
    const updatedCriteres = criteres.map((critere, index) => ({
      ...critere,
      ponderation_pct: equalWeight + (index < remainder ? 1 : 0)
    }));
    setCriteres(updatedCriteres);
  };
  return <div className="space-y-4">
      <div className="flex justify-between items-center mb-2">
        <div>
          <p className="text-sm text-muted-foreground">
            Total des pondérations: <span className={`font-medium ${totalPonderation === 100 ? 'text-green-600' : 'text-destructive'}`}>
              {totalPonderation}%
            </span>
            {totalPonderation !== 100 && <span className="text-xs text-destructive ml-1">
                (doit être égal à 100%)
              </span>}
          </p>
        </div>
        <Button type="button" variant="default" size="sm" onClick={distributeEqually} disabled={criteres.length === 0}>
          Répartir équitablement - Critères
        </Button>
      </div>

      <div className="space-y-3">
        {criteres.map((critere, index) => <Card key={index} className="border border-border/60">
            <CardContent className="pt-4 bg-gray-50">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Critère {index + 1}</span>
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeCritere(index)} className="text-destructive hover:text-destructive">
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
                  
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Nom du critère *</Label>
                    <Input value={critere.nom} onChange={e => updateCritere(index, 'nom', e.target.value)} placeholder="Ex: Compétences techniques" required />
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs">Description</Label>
                    <Input value={critere.description} onChange={e => updateCritere(index, 'description', e.target.value)} placeholder="Description du critère" />
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs">Pondération (%) <span className="text-red-500">*</span></Label>
                    <Input type="number" min="1" max="100" value={critere.ponderation_pct} onChange={e => updateCritere(index, 'ponderation_pct', parseInt(e.target.value) || 0)} />
                  </div>
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs">Contenu détaillé du critère <span className="text-red-500">*</span></Label>
                  <Textarea value={critere.contenu_html} onChange={e => updateCritere(index, 'contenu_html', e.target.value)} placeholder="Décrivez en détail ce qui sera évalué pour ce critère..." className="min-h-[60px] text-sm" rows={2} />
                  <p className="text-xs text-muted-foreground">
                    Ce contenu sera utilisé par l'IA pour évaluer les candidatures selon ce critère.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>)}
        
        {criteres.length === 0 && <div className="text-center py-6 text-muted-foreground border border-dashed rounded-md">
            <p className="text-sm">Aucun critère défini pour cette évaluation.</p>
            <p className="text-sm">Cliquez sur "Ajouter un critère" pour commencer.</p>
          </div>}
      </div>

      <div className="flex justify-end pt-4">
        <Button type="button" variant="outline" size="sm" onClick={addCritere} className="bg-blue-200 hover:bg-blue-100">
          <Plus className="h-4 w-4 mr-2" />
          Ajouter un critère
        </Button>
      </div>
    </div>;
};